import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtEUR, fmtDate, fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Phone, ArrowLeft, ChevronRight, SkipForward, RefreshCw, ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Belmodus() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const taak = params.get("taak");
  const [filter, setFilter] = useState(params.get("filter") || "vandaag");
  const [taken, setTaken] = useState([]);
  const [idx, setIdx] = useState(0);
  const [ctx, setCtx] = useState(null);
  const [uitkomsten, setUitkomsten] = useState([]);
  const [notitie, setNotitie] = useState("");
  const [terugbellen, setTerugbellen] = useState("");
  const [scriptOpen, setScriptOpen] = useState(true);
  const [busy, setBusy] = useState(false);
  const [klaar, setKlaar] = useState(0);
  const [tellers, setTellers] = useState(null);

  const laad = useCallback(async () => {
    const p = { filter }; if (params.get("campagne_id")) p.campagne_id = params.get("campagne_id");
    const [r, i] = await Promise.all([
      api.get("/beltaken", { params: taak ? { filter: "open" } : p }),
      api.get("/instellingen/campagnes"),
    ]);
    let lijst = r.data.filter(b => b.status !== "afgerond");
    if (taak) { const t = lijst.find(b => b.id === taak); lijst = t ? [t, ...lijst.filter(b => b.id !== taak)] : lijst; }
    setTaken(lijst); setIdx(0); setKlaar(0);
    setUitkomsten((i.data.uitkomsten || []).filter(u => u.code !== "niet_bereikbaar"));
  }, [filter, taak, params]);
  useEffect(() => { laad(); }, [laad]);
  useEffect(() => { api.get("/beltaken/tellers").then(r => setTellers(r.data)).catch(() => {}); }, []);

  const huidige = taken[idx];
  const laadCtx = useCallback(async () => { if (!huidige) return; setCtx(null); const { data } = await api.get(`/beltaken/${huidige.id}/context`); setCtx(data); }, [huidige]);
  useEffect(() => { laadCtx(); setNotitie(""); setTerugbellen(""); }, [laadCtx]);

  const volgende = useCallback(() => { setIdx(i => i + 1); }, []);
  const opslaan = useCallback(async (code) => {
    if (!huidige || busy) return;
    if (code === "terugbellen" && !terugbellen) { toast.error("Kies een terugbel-moment"); return; }
    setBusy(true);
    try {
      await api.post(`/beltaken/${huidige.id}/uitkomst`, { uitkomst: code, notitie, terugbellen_op: terugbellen ? new Date(terugbellen).toISOString() : null });
      toast.success(`Opgeslagen: ${uitkomsten.find(u => u.code === code)?.label || code}`); setKlaar(k => k + 1); volgende();
    } catch (e) { toast.error(e?.response?.data?.detail || "Opslaan mislukt"); } finally { setBusy(false); }
  }, [huidige, busy, notitie, terugbellen, uitkomsten, volgende]);

  useEffect(() => {
    const h = (e) => { if (["INPUT", "TEXTAREA"].includes(e.target.tagName)) return;
      const n = parseInt(e.key, 10); if (n >= 1 && n <= uitkomsten.length) opslaan(uitkomsten[n - 1].code);
      if (e.key.toLowerCase() === "n" || e.key.toLowerCase() === "s") volgende(); };
    window.addEventListener("keydown", h); return () => window.removeEventListener("keydown", h);
  }, [uitkomsten, opslaan, volgende]);

  const FILTERS = [
    { key: "vandaag", label: "Vandaag", n: tellers?.vandaag },
    { key: "gepland", label: "Gepland", n: tellers?.gepland },
    { key: "open", label: "Alle open" },
  ];
  const PeriodeSwitch = () => taak ? null : (
    <div className="flex items-center justify-center">
      <div className="inline-flex items-center gap-1 rounded-lg bg-secondary p-1" data-testid="belmodus-filter">
        {FILTERS.map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`belmodus-filter-${f.key}`}
            className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${filter === f.key ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>
            {f.label}{typeof f.n === "number" ? ` · ${f.n}` : ""}
          </button>
        ))}
      </div>
    </div>
  );

  if (taken.length === 0) return (
    <div className="max-w-xl mx-auto text-center py-16 space-y-4" data-testid="belmodus-leeg">
      <PeriodeSwitch />
      <p className="text-muted-foreground">Geen open beltaken in {filter === "vandaag" ? "'Vandaag'" : filter === "gepland" ? "'Gepland'" : "deze selectie"}.{taak ? "" : " Kies hierboven een andere periode."}</p>
      <Button variant="outline" onClick={() => navigate("/agenda")}><ArrowLeft className="h-4 w-4 mr-1" />Naar agenda</Button>
    </div>
  );
  if (!huidige) return <div className="max-w-xl mx-auto text-center py-16 space-y-3" data-testid="belmodus-klaar"><h2 className="text-xl font-semibold">Klaar — {klaar} van {taken.length} afgehandeld</h2><Button onClick={() => navigate("/agenda")}><ArrowLeft className="h-4 w-4 mr-1" />Naar agenda</Button></div>;

  const s = ctx?.stats || {};
  const org = ctx?.organisatie || {};
  const contact = ctx?.contact;
  return (
    <div className="max-w-2xl mx-auto space-y-4" data-testid="belmodus">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => navigate("/agenda")} data-testid="belmodus-terug"><ArrowLeft className="h-4 w-4 mr-1" />Agenda</Button>
        <span className="text-sm text-muted-foreground" data-testid="belmodus-voortgang">{idx + 1} van {taken.length}</span>
        <Button variant="ghost" size="sm" onClick={volgende} data-testid="belmodus-overslaan"><SkipForward className="h-4 w-4 mr-1" />Overslaan (S)</Button>
      </div>
      <PeriodeSwitch />

      <div className="bg-card border border-border rounded-2xl p-5 space-y-4 shadow-sm">
        <div>
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{huidige.campagne_naam || "Handmatig"} · prio {huidige.prioriteit}</div>
          <h1 className="text-2xl font-semibold leading-tight"><Link to={`/organisaties/${huidige.organisatie_id}`} className="hover:text-primary" data-testid="belmodus-org">{huidige.organisatie_naam}</Link></h1>
          <div className="text-sm text-muted-foreground">{contact ? `${contact.voornaam} ${contact.achternaam}${contact.functie ? ` · ${contact.functie}` : ""}` : "geen contactpersoon"} · {org.plaats} {org.land}</div>
          <a href={`tel:${(huidige.telefoon || "").replace(/\s/g, "")}`} className="mt-2 inline-flex items-center gap-2 text-2xl font-semibold text-primary tabular-nums" data-testid="belmodus-telefoon"><Phone className="h-6 w-6" />{huidige.telefoon || "geen nummer"}</a>
          {ctx?.contacten?.length > 1 && <div className="text-xs text-muted-foreground mt-1">Ook: {ctx.contacten.filter(c => c.telefoon || c.mobiel).map(c => `${c.voornaam} ${c.telefoon || c.mobiel}`).join(" · ")}</div>}
          <p className="mt-2 text-sm bg-amber-50 text-amber-900 border border-amber-200 rounded-lg px-3 py-2" data-testid="belmodus-reden"><strong>Reden:</strong> {huidige.reden}{huidige.aantal_pogingen ? ` · poging ${huidige.aantal_pogingen + 1}` : ""}</p>
        </div>

        {ctx ? (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm" data-testid="belmodus-context">
            <div className="rounded-lg bg-muted/40 p-2"><div className="text-[10px] uppercase text-muted-foreground">Laatste order</div>{s.laatste_order ? <>{fmtDate(s.laatste_order.datum)} · {fmtEUR(s.laatste_order.bedrag)}</> : s.laatste_order_datum ? fmtDate(s.laatste_order_datum) : "—"}</div>
            <div className="rounded-lg bg-muted/40 p-2"><div className="text-[10px] uppercase text-muted-foreground">Omzet 12m</div>{fmtEUR(s.omzet_12m)} · {s.aantal_orders_12m} orders</div>
            <div className="rounded-lg bg-muted/40 p-2"><div className="text-[10px] uppercase text-muted-foreground">Webshop</div><Badge variant="outline" className="h-5">{s.webshop_status}</Badge>
              {ctx.uitnodiging && <div className="text-[11px] text-muted-foreground mt-1">uitnodiging {fmtDate(ctx.uitnodiging.sent_at)} ({ctx.uitnodiging.status})
                {ctx.uitnodiging.status === "verstuurd" && <button className="ml-1 text-primary hover:underline" onClick={async () => { try { await api.post(`/beltaken/${huidige.id}/uitnodiging-opnieuw`); toast.success("Uitnodiging opnieuw verstuurd"); } catch (e) { toast.error(e?.response?.data?.detail || "Mislukt"); } }} data-testid="belmodus-uitnodiging-opnieuw"><RefreshCw className="h-3 w-3 inline" /> opnieuw</button>}</div>}</div>
            <div className="rounded-lg bg-muted/40 p-2 col-span-2 md:col-span-3"><div className="text-[10px] uppercase text-muted-foreground">Kenmerken</div><div className="flex flex-wrap gap-1 mt-0.5">{(ctx.kenmerken || []).slice(0, 12).map(k => <Badge key={k} variant="outline" className="h-4 text-[10px]">{k}</Badge>)}{!ctx.kenmerken?.length && "—"}</div></div>
            {ctx.activiteiten?.length > 0 && <div className="rounded-lg bg-muted/40 p-2 col-span-2 md:col-span-3"><div className="text-[10px] uppercase text-muted-foreground">Laatste activiteit</div>{ctx.activiteiten[0].onderwerp} · {fmtDateTime(ctx.activiteiten[0].datum)}</div>}
            {huidige.context_samenvatting && <div className="col-span-2 md:col-span-3 text-xs text-muted-foreground">{huidige.context_samenvatting}</div>}
          </div>
        ) : <div className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Context laden…</div>}

        {huidige.script && (
          <div className="rounded-lg border border-border">
            <button className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium" onClick={() => setScriptOpen(o => !o)} data-testid="belmodus-script-toggle">Belscript {scriptOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}</button>
            {scriptOpen && <pre className="px-3 pb-3 text-sm whitespace-pre-wrap font-sans leading-relaxed" data-testid="belmodus-script">{huidige.script}</pre>}
          </div>
        )}

        <div className="space-y-2">
          <Textarea rows={2} placeholder="Notitie…" value={notitie} onChange={e => setNotitie(e.target.value)} data-testid="belmodus-notitie" />
          <div className="flex items-center gap-2 text-xs"><span className="text-muted-foreground">Terugbellen op:</span><Input type="datetime-local" className="h-8 w-56" value={terugbellen} onChange={e => setTerugbellen(e.target.value)} data-testid="belmodus-terugbellen" /></div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="belmodus-uitkomsten">
            {uitkomsten.map((u, i) => (
              <Button key={u.code} variant={u.code.startsWith("bereikt_positief") ? "default" : "outline"} className="h-auto py-2 flex-col items-start text-left" disabled={busy} onClick={() => opslaan(u.code)} data-testid={`uitkomst-${u.code}`}>
                <span className="text-[10px] opacity-60">{i + 1}</span><span className="text-xs font-medium leading-tight">{u.label}</span>
              </Button>))}
          </div>
        </div>
      </div>
      <div className="text-center text-xs text-muted-foreground">Sneltoetsen: 1–{uitkomsten.length} uitkomst · S overslaan <ChevronRight className="h-3 w-3 inline" /></div>
    </div>
  );
}
